import {
  useBrokersServiceBrokersUsersInviteCreate,
  useBrokersServiceBrokersUsersListKey,
  useBrokersServiceBrokersUsersPartialUpdate,
  useBrokersServiceBrokersUsersRetrieveKey
} from '@insurance/api/queries'
import { BrokerTeamSelect } from '@insurance/components'
import { useValidationsField } from '@insurance/hooks'
import { PhoneInput } from '@shared/components'
import { useSessionContext, useWizardContext } from '@shared/hooks'
import { showApiErrors } from '@shared/utils'
import { useQueryClient } from '@tanstack/react-query'
import { App, Col, Form, Input, Modal, Row } from 'antd'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { SetAgentStatus } from './SetAgentStatus'
import { CommonPropsAgent } from './types'

export const EditAddAgent = ({
  data,
  brokerId,
  onSuccess,
  ...props
}: CommonPropsAgent) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { message } = App.useApp()
  const [form] = Form.useForm()
  const { required, personalCode, phoneNumber } = useValidationsField()
  const { onChangeStep, current } = useWizardContext()
  const { broker } = useSessionContext()

  useEffect(() => {
    if (!data) return form.resetFields()

    const dataValues = {
      ...data,
      teams: data?.teams?.map((team) => team.id)
    }

    form.setFieldsValue(dataValues)
  }, [data, form, props.open])

  const onSuccessRequest = () => {
    queryClient.invalidateQueries({
      queryKey: [useBrokersServiceBrokersUsersListKey]
    })
    queryClient.invalidateQueries({
      queryKey: [useBrokersServiceBrokersUsersRetrieveKey, { id: data?.id }]
    })

    message.success(data ? t('edited_successfully') : t('added_successfully'))

    onSuccess()
    if (current < 5) {
      onChangeStep(4)
    }
  }

  const { mutate: mutateEdit, isPending: isEditPending } =
    useBrokersServiceBrokersUsersPartialUpdate({
      onSuccess: onSuccessRequest,
      onError: (error) => showApiErrors(error, form)
    })

  const { mutate: mutateInvite, isPending: isInvitePending } =
    useBrokersServiceBrokersUsersInviteCreate({
      onSuccess: onSuccessRequest,
      onError: (error) => showApiErrors(error, form)
    })

  const onSubmit = async () => {
    const values = await form.validateFields()

    const dataValues = {
      ...values
    }

    if (data?.id) mutateEdit({ id: data.id, requestBody: dataValues })
    else mutateInvite({ requestBody: dataValues })
  }

  const isPending = isEditPending || isInvitePending

  return (
    <Modal
      title={data ? t('edit_agent') : t('add_agent')}
      onOk={onSubmit}
      okButtonProps={{ loading: isPending }}
      okText={t('save')}
      width={800}
      {...props}
    >
      <Form layout="vertical" form={form}>
        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item
              name="last_name"
              label={t('last_name')}
              rules={[required]}
            >
              <Input placeholder={t('last_name')} />
            </Form.Item>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item
              name="first_name"
              label={t('first_name')}
              rules={[required]}
            >
              <Input placeholder={t('first_name')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} lg={12}>
            <Form.Item
              name="email"
              label={t('email')}
              rules={[required, { type: 'email' }]}
            >
              <Input type="email" placeholder={t('email')} disabled={!!data} />
            </Form.Item>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item
              name="identification_number"
              label={t('identification_number')}
              normalize={(value, prevValue) =>
                String(value).length > 13 ? prevValue : value
              }
              rules={[required, personalCode]}
            >
              <Input
                disabled={!!data}
                type="number"
                min={0}
                placeholder={t('identification_number')}
              />
            </Form.Item>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item name="phone" label={t('phone')} rules={[phoneNumber]}>
              <PhoneInput />
            </Form.Item>
          </Col>

          <Col xs={24} lg={12}>
            <Form.Item name={data ? 'teams' : 'team'} label={t('teams')}>
              <BrokerTeamSelect
                broker={brokerId || String(broker?.user?.broker?.id)}
                placeholder={data ? t('teams') : t('team')}
                mode={data ? 'multiple' : undefined}
              />
            </Form.Item>
          </Col>
        </Row>

        {data && (
          <Row gutter={24}>
            <Col span={24}>
              <Form.Item label={t('active')}>
                <SetAgentStatus data={data} />
              </Form.Item>
            </Col>
          </Row>
        )}
      </Form>
    </Modal>
  )
}
